import type {
  ImportRow,
  Registration,
  RegistrationCategory,
  RegistrationTeam,
} from '../services/registrations';
import { isUnderConsentAge } from '../utils/consent-age';

/**
 * The entry list as a spreadsheet.
 *
 * One column per field, headed the way the panel names it, and read back
 * tolerant of whatever a club calls the same thing — "Società" or "Palestra"
 * for the squad, "Nato il" for the date of birth — because the file that comes
 * in was typed up by somebody who never saw ours. The export writes an `Id` column; a file that
 * still has it corrects those entries rather than adding them again.
 *
 * The spreadsheet library is loaded on demand: it is a third of a megabyte
 * and most desks never open this dialog.
 *
 * The gender of the entrant's class goes in and out in a column of its own,
 * spelled however a club spells it ("M", "Femmina", "Male" — GENDER_SPELLINGS,
 * the server's list too). The turn booked goes out only, like the payment: a
 * turn is a stage's own round, booked at the desk or by the athlete, so a file
 * never books one.
 */

/** The panel's translator, narrowed to what a sheet needs of it. */
export type Translate = (key: string, options?: Record<string, unknown>) => string;

export type SheetField =
  | 'id'
  | 'name'
  | 'surname'
  | 'email'
  | 'guardian_email'
  | 'category'
  | 'team'
  | 'birth_date'
  | 'gender'
  | 'turn'
  | 'paid'
  | 'confirmed';

interface ColumnSpec {
  field: SheetField;
  /** The header the export writes, by i18n key. */
  labelKey: string;
  /** Other headers taken for this column, in any language a club writes. */
  aliases: string[];
  /** A date cell: read as one, written as one. */
  date?: boolean;
  /** Written by the export for the record, never read back. */
  exportOnly?: boolean;
}

export const COLUMNS: readonly ColumnSpec[] = [
  { field: 'id', labelKey: 'gymAdmin.registrations.sheet.id', aliases: ['id'] },
  {
    field: 'name',
    labelKey: 'gymAdmin.members.fields.name',
    aliases: ['nome', 'name', 'first name', 'firstname'],
  },
  {
    field: 'surname',
    labelKey: 'gymAdmin.members.fields.surname',
    aliases: ['cognome', 'surname', 'last name', 'lastname'],
  },
  { field: 'email', labelKey: 'gymAdmin.members.fields.email', aliases: ['email', 'e mail', 'mail'] },
  {
    field: 'guardian_email',
    labelKey: 'gymAdmin.registrations.guardianEmail',
    aliases: [
      'email genitore',
      'email tutore',
      'email del genitore',
      'email genitore tutore',
      'mail genitore',
      'guardian email',
      'parent email',
      'parent guardian email',
    ],
  },
  {
    field: 'category',
    labelKey: 'gymAdmin.members.fields.category',
    aliases: ['categoria', 'category'],
  },
  // The squad doubles as the club a file calls "Società" or "Palestra": that is
  // what the column used to be, and it is the one list an entry points at now.
  {
    field: 'team',
    labelKey: 'gymAdmin.members.fields.team',
    aliases: ['squadra', 'team', 'palestra', 'societa', 'club', 'gym', 'team club'],
  },
  {
    field: 'birth_date',
    labelKey: 'gymAdmin.members.fields.birthDate',
    aliases: ['data di nascita', 'nascita', 'nato il', 'date of birth', 'birth date', 'birthdate', 'dob'],
    date: true,
  },
  {
    field: 'gender',
    labelKey: 'gymAdmin.registrations.gender',
    aliases: ['genere', 'gender', 'sesso', 'sex', 'm f'],
  },
  { field: 'turn', labelKey: 'gymAdmin.registrations.turn', aliases: [], exportOnly: true },
  { field: 'paid', labelKey: 'gymAdmin.registrations.paid', aliases: [], exportOnly: true },
  { field: 'confirmed', labelKey: 'gymAdmin.registrations.confirmed', aliases: [], exportOnly: true },
];

/** The columns the import reads: everything but what is written for the record. */
export type ImportField = Exclude<SheetField, 'turn' | 'paid' | 'confirmed'>;

/**
 * How a sheet may spell the gender of a class, without regard to case: the
 * letter, the words an Italian club types, the English ones. The server reads
 * the same list (CompetitionEntryImport::GENDERS); anything else is refused
 * rather than guessed at.
 */
export const GENDER_SPELLINGS: Readonly<Record<string, 'male' | 'female'>> = {
  m: 'male',
  maschio: 'male',
  maschile: 'male',
  male: 'male',
  f: 'female',
  femmina: 'female',
  femminile: 'female',
  female: 'female',
};

/** A gender cell as male or female; '' for an empty one, null for one we cannot read. */
export function readGenderCell(value: unknown): 'male' | 'female' | '' | null {
  const text = String(value ?? '').trim().toLowerCase();
  if (!text) return '';
  return GENDER_SPELLINGS[text] ?? null;
}

/**
 * A header, reduced to what it means: no case, no accents, no punctuation.
 * "Data di Nascita", "data_di_nascita" and "DATA DI NASCITA" are one column.
 */
export function normalizeHeader(header: unknown): string {
  return String(header ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** Which of our columns a sheet header is — ours by label, or any alias. */
export function fieldForHeader(header: unknown, t: Translate): ImportField | null {
  const key = normalizeHeader(header);
  if (!key) return null;

  for (const column of COLUMNS) {
    if (column.exportOnly) continue;
    if (normalizeHeader(t(column.labelKey)) === key || column.aliases.includes(key)) {
      return column.field as ImportField;
    }
  }
  return null;
}

const ISO_DATE = /^(\d{4})-(\d{1,2})-(\d{1,2})$/;
const DAY_FIRST = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/;

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** A JS date as the API wants it, read in local time (see sheet_to_json). */
export function toIsoDate(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * A date cell as ISO, whether the sheet held a real date or text typed the
 * way an Italian desk writes one. Null when it is neither.
 */
export function readDateCell(value: unknown): string | null {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : toIsoDate(value);

  const text = String(value ?? '').trim();
  if (!text) return '';

  const iso = text.match(ISO_DATE);
  if (iso) return `${iso[1]}-${pad(Number(iso[2]))}-${pad(Number(iso[3]))}`;

  const dayFirst = text.match(DAY_FIRST);
  if (dayFirst) return `${dayFirst[3]}-${pad(Number(dayFirst[2]))}-${pad(Number(dayFirst[1]))}`;

  return null;
}

/** An ISO date back to a real date cell, so Excel sorts and formats it. */
function toDateCell(iso: string | null | undefined): Date | '' {
  if (!iso) return '';
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d);
}

export interface SheetProblem {
  row: number;
  message: string;
}

export interface ParsedSheet {
  rows: ImportRow[];
  /** The fields the sheet had a column for. */
  fields: SheetField[];
  /** Headers that are none of ours, left where they were. */
  unknownHeaders: string[];
  /** Cells that could not be read as what the column holds. */
  problems: SheetProblem[];
}

/**
 * Reads the first sheet of a workbook into rows, one per line under the
 * header. A header we cannot place is reported and its column skipped; a
 * line with nothing in it is skipped without a word.
 */
export async function readEntrySheet(file: File, t: Translate): Promise<ParsedSheet> {
  const XLSX = await import('xlsx');
  const workbook = XLSX.read(await fileBytes(file), { type: 'array', cellDates: true });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  if (!sheet) return { rows: [], fields: [], unknownHeaders: [], problems: [] };

  const matrix = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    raw: true,
    defval: '',
    blankrows: false,
  });
  if (matrix.length === 0) return { rows: [], fields: [], unknownHeaders: [], problems: [] };

  const [headers, ...lines] = matrix;
  const mapping: { index: number; field: ImportField }[] = [];
  const unknownHeaders: string[] = [];
  const seen = new Set<ImportField>();

  headers.forEach((header, index) => {
    const text = String(header ?? '').trim();
    if (!text) return;
    const field = fieldForHeader(text, t);
    // The first column wins when a sheet names the same thing twice.
    if (field && !seen.has(field)) {
      seen.add(field);
      mapping.push({ index, field });
    } else {
      unknownHeaders.push(text);
    }
  });

  const rows: ImportRow[] = [];
  const problems: SheetProblem[] = [];
  const specs = new Map(COLUMNS.map((c) => [c.field, c]));

  lines.forEach((cells, offset) => {
    // Sheet rows are 1-based and the header is row 1.
    const rowNumber = offset + 2;
    const row: ImportRow = { row: rowNumber };
    let filled = false;

    for (const { index, field } of mapping) {
      const raw = cells[index];
      let value: string;
      if (specs.get(field)?.date) {
        const iso = readDateCell(raw);
        if (iso === null) {
          problems.push({
            row: rowNumber,
            message: t('gymAdmin.registrations.importErrors.invalidDate', {
              value: String(raw ?? '').trim(),
            }),
          });
          continue;
        }
        value = iso;
      } else {
        value = cellText(raw);
      }
      if (value) filled = true;
      row[field] = value;
    }

    if (filled) rows.push(row);
  });

  return { rows, fields: mapping.map((m) => m.field), unknownHeaders, problems };
}

/** The file's bytes; older WebViews (and jsdom) have no `Blob#arrayBuffer`. */
function fileBytes(file: File): Promise<ArrayBuffer> {
  if (typeof file.arrayBuffer === 'function') return file.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
}

/** A cell as the text a person typed — a number typed as one comes back intact. */
function cellText(raw: unknown): string {
  if (raw instanceof Date) return toIsoDate(raw);
  return String(raw ?? '').trim();
}

export type PreviewStatus = 'new' | 'update';

export interface PreviewRow {
  row: ImportRow;
  status: PreviewStatus;
  /** The entry this row corrects, when it is one. */
  existing?: Registration;
  /** Why this row cannot go in, in the desk's language. */
  error?: string;
}

export interface ImportPreview {
  rows: PreviewRow[];
  created: number;
  updated: number;
  errors: number;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Who a row with no address is: name, surname and date of birth together —
 * the server's rule (CompetitionParticipant#identity_key), so the preview
 * matches what the import will do. Null when the row has too little to say.
 */
export function identityKey(
  name: string | null | undefined,
  surname: string | null | undefined,
  birthDate: string | null | undefined
): string | null {
  const first = (name ?? '').trim().toLowerCase();
  const last = (surname ?? '').trim().toLowerCase();
  const born = (birthDate ?? '').trim().slice(0, 10);
  if (!born || (!first && !last)) return null;
  return `identity:${first}|${last}|${born}`;
}

/**
 * What the file will do before any of it is sent: which rows enter somebody,
 * which correct an entry already here, and which cannot go in at all. The
 * same rules the server applies, run here first, because a refused file
 * costs the desk a round trip and the server refuses the whole of it.
 *
 * `genderCollected` false — the organiser's agreement does not cover the
 * gender — leaves the gender column unread, as the server does.
 */
export function previewImport(
  parsed: ParsedSheet,
  registrations: Registration[],
  categories: RegistrationCategory[],
  teams: RegistrationTeam[],
  t: Translate,
  genderCollected = true
): ImportPreview {
  const byId = new Map(registrations.map((r) => [r.id, r]));
  const byEmail = new Map(
    registrations.filter((r) => r.email).map((r) => [r.email!.toLowerCase(), r])
  );
  // The entries with no address — children on a guardian's — by who they are,
  // the way the server matches them (CompetitionParticipant#identity_key).
  const byIdentity = new Map(
    registrations
      .filter((r) => !r.email)
      .map((r) => [identityKey(r.name, r.surname, r.birth_date), r] as const)
      .filter((pair): pair is readonly [string, Registration] => pair[0] !== null)
  );
  const categoryNames = new Set(categories.map((c) => c.name.trim().toLowerCase()));
  const teamNames = new Set(teams.map((c) => c.name.trim().toLowerCase()));
  const problemsByRow = new Map(parsed.problems.map((p) => [p.row, p.message]));
  const seen = new Map<string, number>();

  const rows = parsed.rows.map((row): PreviewRow => {
    const email = (row.email ?? '').trim().toLowerCase();
    const guardianEmail = (row.guardian_email ?? '').trim().toLowerCase();
    const identity = identityKey(row.name, row.surname, row.birth_date);
    const minor = isUnderConsentAge(row.birth_date);
    let existing: Registration | undefined;
    let error = problemsByRow.get(row.row);

    if (row.id) {
      existing = byId.get(String(row.id));
      if (!existing) error ??= t('gymAdmin.registrations.importErrors.unknownId', { id: row.id });
    } else if (email) {
      existing = byEmail.get(email);
    } else if (identity) {
      existing = byIdentity.get(identity);
    }

    // A child under 14 is entered on the guardian's address and needs no
    // email of their own; everybody else is named by their address.
    if (minor && !guardianEmail) {
      error ??= t('gymAdmin.registrations.importErrors.missingGuardianEmail');
    } else if (!row.id && !email && !minor) {
      error ??= t('gymAdmin.registrations.importErrors.missingEmail');
    } else if (email && !EMAIL.test(email)) {
      error ??= t('gymAdmin.registrations.importErrors.invalidEmail');
    } else if (guardianEmail && !EMAIL.test(guardianEmail)) {
      error ??= t('gymAdmin.registrations.importErrors.invalidGuardianEmail');
    }

    const category = (row.category ?? '').trim();
    if (category && !categoryNames.has(category.toLowerCase())) {
      error ??= t('gymAdmin.registrations.importErrors.unknownCategory', { name: category });
    }
    const team = (row.team ?? '').trim();
    if (team && !teamNames.has(team.toLowerCase())) {
      error ??= t('gymAdmin.registrations.importErrors.unknownTeam', { name: team });
    }
    if (genderCollected && readGenderCell(row.gender) === null) {
      error ??= t('gymAdmin.registrations.importErrors.invalidGender', { value: (row.gender ?? '').trim() });
    }

    const key = existing ? `id:${existing.id}` : email ? `email:${email}` : identity;
    if (key) {
      const first = seen.get(key);
      if (first !== undefined) {
        error ??= t('gymAdmin.registrations.importErrors.duplicate', { row: first });
      } else {
        seen.set(key, row.row);
      }
    }

    return { row, status: existing ? 'update' : 'new', existing, error };
  });

  return {
    rows,
    created: rows.filter((r) => !r.error && r.status === 'new').length,
    updated: rows.filter((r) => !r.error && r.status === 'update').length,
    errors: rows.filter((r) => r.error).length,
  };
}

/**
 * What a written sheet holds beyond the fixed columns. `payments` is whether
 * the competition takes an entry fee: without one there is no "paid" column,
 * as there is none on the page.
 */
export interface SheetOptions {
  payments?: boolean;
}

/** The columns a written sheet has, in order. */
function sheetColumns({ payments = true }: SheetOptions = {}): readonly ColumnSpec[] {
  return payments ? COLUMNS : COLUMNS.filter((column) => column.field !== 'paid');
}

/** The header row, in the desk's language. */
export function sheetHeaders(t: Translate, options?: SheetOptions): string[] {
  return sheetColumns(options).map((c) => t(c.labelKey));
}

/** The entry list as rows of cells, ready for a sheet. Dates are real dates. */
export function entryRows(
  registrations: Registration[],
  t: Translate,
  options?: SheetOptions
): (string | Date)[][] {
  const columns = sheetColumns(options);
  return registrations.map((r) =>
    columns.map((column) => {
      switch (column.field) {
        case 'id':
          return r.id;
        case 'name':
          return r.name ?? '';
        case 'surname':
          return r.surname ?? '';
        case 'email':
          return r.email ?? '';
        case 'guardian_email':
          return r.guardian_email ?? '';
        case 'category':
          return r.category_name ?? '';
        case 'team':
          return r.team_name ?? '';
        case 'birth_date':
          return toDateCell(r.birth_date);
        // Written as the panel says it, which the import reads back as it is.
        case 'gender':
          return r.gender ? t(`gymAdmin.registrations.genders.${r.gender}`) : '';
        case 'turn':
          return (
            r.competition_round_name ?? (r.turn_to_choose ? t('gymAdmin.registrations.turnToChoose') : '')
          );
        case 'paid':
          return r.paid ? t('gymAdmin.registrations.yes') : t('gymAdmin.registrations.no');
        case 'confirmed':
          return r.confirmed ? t('gymAdmin.registrations.yes') : t('gymAdmin.registrations.no');
      }
    })
  );
}

/**
 * Made-up entrants showing what goes where. The category and squad are this
 * competition's own when it has any, so the example imports as it is — and the
 * third row is a child under 14, on a guardian's address and with none of
 * their own, because that is the shape a desk gets wrong without an example.
 *
 * Built from the column list rather than by position: a new column must not
 * silently shift every cell of the template one place to the left.
 */
export function exampleRows(
  categories: RegistrationCategory[],
  teams: RegistrationTeam[],
  options?: SheetOptions
): (string | Date)[][] {
  const category = categories[0]?.name ?? 'Junior';
  const team = teams[0]?.name ?? '';
  const under14 = new Date(new Date().getFullYear() - 9, 4, 22);

  const examples: Partial<Record<SheetField, string | Date>>[] = [
    {
      name: 'Anna',
      surname: 'Bianchi',
      email: 'anna.bianchi@example.com',
      category,
      team: team || 'CAI Monza',
      birth_date: new Date(2008, 2, 14),
      gender: 'F',
    },
    {
      name: 'Marco',
      surname: 'Rossi',
      email: 'marco.rossi@example.com',
      category: categories[1]?.name ?? category,
      team: teams[1]?.name ?? '',
      birth_date: new Date(1990, 0, 1),
      gender: 'M',
    },
    {
      name: 'Luca',
      surname: 'Verdi',
      guardian_email: 'genitore@example.com',
      category,
      team: team || 'CAI Monza',
      birth_date: under14,
      gender: 'M',
    },
  ];

  const columns = sheetColumns(options);
  return examples.map((example) => columns.map((column) => example[column.field] ?? ''));
}

/** Hands the browser a workbook of these rows under the given file name. */
async function downloadSheet(
  headers: string[],
  rows: (string | Date)[][],
  sheetName: string,
  filename: string
): Promise<void> {
  const XLSX = await import('xlsx');
  const sheet = XLSX.utils.aoa_to_sheet([headers, ...rows], { cellDates: true });
  sheet['!cols'] = headers.map((header, index) => ({
    wch: Math.max(header.length, ...rows.map((row) => String(row[index] ?? '').length), 8) + 2,
  }));
  const workbook = XLSX.utils.book_new();
  // Excel caps a sheet name at 31 characters.
  XLSX.utils.book_append_sheet(workbook, sheet, sheetName.slice(0, 31));
  XLSX.writeFile(workbook, filename, { compression: true });
}

export async function downloadEntries(
  registrations: Registration[],
  stageSlug: string,
  t: Translate,
  options?: SheetOptions
): Promise<void> {
  await downloadSheet(
    sheetHeaders(t, options),
    entryRows(registrations, t, options),
    t('gymAdmin.registrations.title'),
    t('gymAdmin.registrations.sheet.exportFileName', { slug: stageSlug })
  );
}

export async function downloadTemplate(
  categories: RegistrationCategory[],
  teams: RegistrationTeam[],
  t: Translate,
  options?: SheetOptions
): Promise<void> {
  await downloadSheet(
    sheetHeaders(t, options),
    exampleRows(categories, teams, options),
    t('gymAdmin.registrations.title'),
    t('gymAdmin.registrations.sheet.templateFileName')
  );
}
